import { useCallback, useEffect, useRef, useState } from "react";
import { useReviewOfferEligibility } from "./useReviewOfferEligibility";
import {
  DEFAULT_REVIEW_OFFER_PROGRAM,
  ReviewOfferProgram,
  ReviewOfferRowState,
  ThanosReviewErrorDetail,
  ThanosReviewSubmittedDetail,
} from "./types";

// Contract §1 (thanos-review-v1): "poll GET .../eligibility until entitlement
// appears (or 30s timeout -> S7 error with retry)".
export const APPLYING_TIMEOUT_MS = 30_000;

export type ReviewOfferAnalyticsEvent =
  | "review_offer_viewed"
  | "review_offer_clicked"
  | "review_popup_opened"
  | "review_submitted"
  | "review_discount_applied"
  // HUB-455: 6th event from the frontend spec — the S7 error/timeout path
  // (30s polling timeout OR a thanos:review-error from the widget) previously
  // fired no analytics at all, leaving that failure mode invisible in PostHog.
  | "review_offer_error";

export type UseReviewOfferStateArgs = {
  /** is_review_discount_enabled — caller resolves this so the hook never has to know about PostHog */
  flagEnabled: boolean;
  /** cache-key differentiator only — the eligibility endpoint takes no params (session-resolved) */
  campaignId?: string | number;
  onAnalyticsEvent?: (event: ReviewOfferAnalyticsEvent) => void;
};

export const useReviewOfferState = ({ flagEnabled, campaignId, onAnalyticsEvent }: UseReviewOfferStateArgs) => {
  const [applying, setApplying] = useState(false);
  const [errored, setErrored] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: eligibility,
    isLoading,
    isError,
    refetch,
  } = useReviewOfferEligibility({
    enabled: flagEnabled,
    campaignId,
    isPolling: applying,
  });

  const clearApplyingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Unmount safety net.
  useEffect(() => clearApplyingTimeout, [clearApplyingTimeout]);

  // Entitlement landed while polling -> S4, stop the clock.
  useEffect(() => {
    if (applying && eligibility?.reason === "entitlement_active") {
      clearApplyingTimeout();
      setApplying(false);
      setWidgetOpen(false);
      onAnalyticsEvent?.("review_discount_applied");
    }
  }, [applying, eligibility?.reason, clearApplyingTimeout, onAnalyticsEvent]);

  // Dedup guard for review_offer_error (Greptile, HUB-455 PR #2405): the
  // persistent thanos:review-error listener + the 30s applying-timeout can
  // both race to report the SAME failed attempt (e.g. a late error event
  // arriving after the timeout already recorded S7, or the widget itself
  // firing thanos:review-error more than once). A ref (not React state) is
  // the source of truth here since the setTimeout closure below would
  // otherwise capture a stale `errored` value from whichever render created
  // it. `recordError` is the single transition-into-errored path — reset
  // only when a fresh attempt starts (enterApplying).
  const erroredRef = useRef(false);

  const recordError = useCallback(() => {
    if (erroredRef.current) return; // already recorded for this attempt — dedupe
    erroredRef.current = true;
    clearApplyingTimeout();
    setApplying(false);
    setErrored(true);
    onAnalyticsEvent?.("review_offer_error");
  }, [clearApplyingTimeout, onAnalyticsEvent]);

  const enterApplying = useCallback(() => {
    erroredRef.current = false;
    setErrored(false);
    setApplying(true);
    clearApplyingTimeout();
    timeoutRef.current = setTimeout(recordError, APPLYING_TIMEOUT_MS);
  }, [clearApplyingTimeout, recordError]);

  // DOM event back-channel (optimistic UI only; the review.submitted webhook
  // to oppizi-api is the source of truth per contract §1/§2).
  useEffect(() => {
    if (!flagEnabled) return undefined;

    const onSubmitted = (event: Event) => {
      const detail = (event as CustomEvent<ThanosReviewSubmittedDetail>).detail;
      if (!detail?.review_id) return;
      onAnalyticsEvent?.("review_submitted");
      enterApplying();
    };
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<ThanosReviewErrorDetail>).detail;
      // eslint-disable-next-line no-console
      console.error("[reviewOffer] thanos:review-error", detail);
      recordError();
    };

    window.addEventListener("thanos:review-submitted", onSubmitted);
    window.addEventListener("thanos:review-error", onError);
    return () => {
      window.removeEventListener("thanos:review-submitted", onSubmitted);
      window.removeEventListener("thanos:review-error", onError);
    };
  }, [flagEnabled, enterApplying, recordError, onAnalyticsEvent]);

  const openWidget = useCallback(() => {
    erroredRef.current = false; // new attempt boundary — a fresh error may be recorded again
    setWidgetOpen(true);
    setErrored(false);
    onAnalyticsEvent?.("review_offer_clicked");
  }, [onAnalyticsEvent]);

  const closeWidget = useCallback(() => {
    setWidgetOpen(false);
  }, []);

  const retry = useCallback(() => {
    erroredRef.current = false; // new attempt boundary — a fresh error may be recorded again
    setErrored(false);
    setWidgetOpen(true);
    onAnalyticsEvent?.("review_offer_clicked");
  }, [onAnalyticsEvent]);

  let state: ReviewOfferRowState = "S8";
  if (flagEnabled && !isLoading) {
    if (errored) {
      state = "S7";
    } else if (applying) {
      state = "S3";
    } else if (widgetOpen) {
      state = "S2";
    } else if (eligibility?.reason === "entitlement_active" && eligibility.entitlement) {
      // Covers both S4 (applied this session) and S7-return-window from the
      // spec table ("entitlement exists on load -> drawer opens in S4") —
      // both render identically, driven by the same eligibility reason.
      state = "S4";
    } else if (!isError && eligibility?.eligible) {
      state = "S1";
    } else {
      // ineligible / redeemed / expired / already_paid / flag_off / service_error
      // -> fail closed, render nothing (spec S8).
      state = "S8";
    }
  }

  // HUB-466: which program the current row/copy is about, and the percent to
  // interpolate into it. Same flag-gating rationale as `entitlement` below —
  // a disabled react-query query retains cached data, and a stale program/
  // percent must not survive is_review_discount_enabled flipping off.
  // Defaults (CHECKOUT_5 / undefined) only matter transiently before the
  // first eligibility fetch resolves — S1/S4 (the only percent-rendering
  // states) never show until `!isLoading`, so a real value is always in
  // hand by the time it's used.
  const program: ReviewOfferProgram = (flagEnabled && eligibility?.program) || DEFAULT_REVIEW_OFFER_PROGRAM;
  const discountPercent: number | null = flagEnabled ? eligibility?.discount_percent ?? null : null;

  return {
    state,
    isLoading,
    // Gate on the flag: a disabled react-query query retains cached data, and a
    // stale entitlement must not keep the discount line alive after PostHog
    // turns is_review_discount_enabled off (flag off => zero footprint).
    entitlement: flagEnabled ? eligibility?.entitlement ?? null : null,
    program,
    discountPercent,
    openWidget,
    closeWidget,
    retry,
    refetchEligibility: refetch,
  };
};
